/**
 * DiditVerifyCard — per-client HMLR-compliant identity + AML + PoA
 * verification via Didit.
 *
 * Replaces the SmartSearch AML+SmartDoc flow. Only shows a verification
 * button when a client has an email address and Didit is configured; if
 * Didit isn't configured (env vars missing), renders a subtle banner
 * pointing the admin at the setup instructions.
 *
 * Renders three states:
 *   1. No verification yet → "Verify identity" button + workflow banner
 *   2. Session created, awaiting client → Shows the link to copy + status
 *      chip; Refresh button re-fetches the decision from Didit.
 *   3. Complete (approved / declined / in review) → Full evidence card
 *      with NFC status, liveness method, face-match score, AML hits,
 *      address match, and any compliance failure reasons.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import API, { downloadAuthed } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ShieldCheck, ShieldWarning, IdentificationCard,
  Fingerprint, ArrowsClockwise, Copy, CheckCircle, XCircle, Warning, Envelope, FileText,
} from "@phosphor-icons/react";

function statusStyle(s) {
  const key = (s || "").toLowerCase().replace(/\s|_/g, "");
  if (key === "approved") return { bg: "bg-emerald-100", fg: "text-emerald-800", label: "Approved" };
  if (key === "declined") return { bg: "bg-rose-100", fg: "text-rose-800", label: "Declined" };
  if (key === "inreview") return { bg: "bg-amber-100", fg: "text-amber-800", label: "In Review" };
  if (key === "notstarted") return { bg: "bg-slate-100", fg: "text-slate-700", label: "Not Started" };
  if (key === "inprogress") return { bg: "bg-sky-100", fg: "text-sky-800", label: "In Progress" };
  return { bg: "bg-slate-100", fg: "text-slate-600", label: s || "—" };
}

export default function DiditVerifyCard({ caseId, client }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [health, setHealth] = useState(null);

  const load = useCallback(async () => {
    if (!caseId || !client?.id) return;
    setLoading(true);
    try {
      const { data } = await API.get(`/didit/verifications/${caseId}/${client.id}`);
      setState(data);
    } catch (e) {
      // 404 = never verified — harmless
      if (e?.response?.status !== 404) {
        toast.error(e?.response?.data?.detail || "Failed to load verification");
      }
      setState({ has_verification: false });
    } finally {
      setLoading(false);
    }
  }, [caseId, client?.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let mounted = true;
    API.get("/didit/config-health").then(({ data }) => { if (mounted) setHealth(data); }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  const startVerification = async () => {
    if (!health?.configured) {
      toast.error("Didit isn't configured yet — ask the administrator to set DIDIT_API_KEY / DIDIT_WORKFLOW_ID / DIDIT_WEBHOOK_SECRET.");
      return;
    }
    setStarting(true);
    try {
      const { data } = await API.post("/didit/sessions", { case_id: caseId, client_id: client.id });
      setState({ has_verification: true, ...data, status: data.status });
      toast.success("Verification link generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start verification");
    } finally {
      setStarting(false);
      load();
    }
  };

  const refresh = async () => {
    if (!state?.session_id) return;
    try {
      await API.post(`/didit/sessions/${state.session_id}/refresh`);
      toast.success("Decision refreshed from Didit");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    }
  };

  const [approvingName, setApprovingName] = useState(false);
  const approveNameReview = async () => {
    if (!state?.session_id) return;
    setApprovingName(true);
    try {
      await API.post(`/didit/sessions/${state.session_id}/approve-name-review`);
      toast.success("Name confirmed — ID check cleared");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't clear the name review");
    } finally {
      setApprovingName(false);
    }
  };

  // Attach Didit's report PDF (works while In Review) so the solicitor can
  // read the full check in-app before deciding — no Didit console needed.
  const [attaching, setAttaching] = useState(false);
  const attachReport = async () => {
    if (!state?.session_id) return;
    setAttaching(true);
    const tid = toast.loading("Fetching report from Didit…");
    try {
      const { data } = await API.post(`/didit/sessions/${state.session_id}/report`);
      toast.success("Report attached to the case", { id: tid });
      if (data?.report_doc_id) {
        await downloadAuthed(`/documents/${data.report_doc_id}/download`, "ID-verification-report.pdf").catch(() => {});
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't fetch the report", { id: tid });
    } finally {
      setAttaching(false);
    }
  };

  // Approve / decline an In-Review check in one click — pushes the decision
  // to Didit AND updates onboarding simultaneously.
  const [deciding, setDeciding] = useState(false);
  const decideReview = async (decision) => {
    if (!state?.session_id) return;
    if (decision === "Declined" && !window.confirm("Decline this ID check? The client will be marked as failed verification.")) return;
    setDeciding(true);
    const tid = toast.loading(decision === "Approved" ? "Approving with Didit…" : "Declining with Didit…");
    try {
      await API.post(`/didit/sessions/${state.session_id}/approve-check`, { decision });
      toast.success(decision === "Approved" ? "Check approved — Didit & onboarding updated" : "Check declined", { id: tid });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update the check", { id: tid });
    } finally {
      setDeciding(false);
    }
  };

  const copyLink = async () => {
    if (!state?.url) return;
    try {
      await navigator.clipboard.writeText(state.url);
      toast.success("Verification link copied to clipboard");
    } catch {
      toast.error("Couldn't copy — link: " + state.url);
    }
  };

  /**
   * Build the ready-to-paste email/message a solicitor sends to the
   * client. Clear NFC-mandatory language up front to reduce the
   * "client skipped NFC, check fails compliance, we redo it"
   * friction loop. All text is UK-focused and passes plain-English
   * readability.
   */
  const buildInstructionsEmail = () => {
    const firstName = client.first_name || "there";
    return (
`Hi ${firstName},

To move your matter forward we need to verify your identity through our secure partner Didit. It takes about 5 minutes on your phone.

Please open this link on your mobile:
${state.url}

You will need:
• Your UK passport (must be biometric — most passports issued after 2010 are)
• A recent utility bill or bank statement (less than 3 months old)

IMPORTANT — do NOT tap "Skip" on any step, especially the NFC passport scan. If you skip the NFC step we cannot accept the check and you will have to start over. If your phone asks you to hold it against the passport, place the top of the phone flat against the photo page and wait 10-15 seconds until it confirms the read.

Please note the check must be completed with your passport — not a driving licence or provisional licence.

If you have any trouble, reply to this email and we will help.

Kind regards`
    );
  };

  const copyInstructions = async () => {
    if (!state?.url) return;
    try {
      await navigator.clipboard.writeText(buildInstructionsEmail());
      toast.success("Client instructions copied — paste into your email");
    } catch {
      toast.error("Couldn't copy — check clipboard permissions");
    }
  };

  const emailInstructions = () => {
    if (!state?.url || !client.email) return;
    const subject = encodeURIComponent("Identity verification — action required");
    const body = encodeURIComponent(buildInstructionsEmail());
    window.location.href = `mailto:${client.email}?subject=${subject}&body=${body}`;
  };

  if (!client?.email) {
    return (
      <Card className="border-slate-200" data-testid={`didit-card-${client.id}`}>
        <CardHeader className="py-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <IdentificationCard weight="fill" className="w-4 h-4 text-indigo-600" />
            Identity verification — Didit
          </CardTitle>
        </CardHeader>
        <CardContent className="py-2 text-xs text-slate-500">
          Add an email to this client before starting identity verification.
        </CardContent>
      </Card>
    );
  }

  // Compliance banner — surfaces the mandatory workflow settings so
  // the solicitor / admin can confirm the Didit workflow is locked
  // down correctly. Renders even when configured — this is intended
  // as an auditor-visible declaration of policy.
  const showConfigBanner = health && (!health.configured || Object.keys(state || {}).length === 0);

  const style = statusStyle(state?.status);
  const evidence = state?.evidence || {};
  const failures = state?.compliance_failures || [];
  const nameReview = state?.name_review || {};
  const canApproveName = nameReview.is_candidate && !state?.name_review_override && state?.approved !== true;

  return (
    <Card className="border-slate-200" data-testid={`didit-card-${client.id}`}>
      <CardHeader className="py-3 pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <IdentificationCard weight="fill" className="w-4 h-4 text-indigo-600" />
            Didit ID + AML + PoA
            {state?.approved === true && (
              <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]">HMLR ✓</Badge>
            )}
            {state?.approved === false && state?.status === "Approved" && (
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]" title={failures.join("\n")}>
                <Warning weight="fill" className="w-2.5 h-2.5 mr-0.5" /> Non-compliant
              </Badge>
            )}
          </CardTitle>
          <Badge className={`${style.bg} ${style.fg} hover:${style.bg} text-[10px] uppercase`}>{style.label}</Badge>
        </div>
      </CardHeader>
      <CardContent className="py-2 space-y-2">
        {showConfigBanner && !health?.configured && (
          <div className="p-2 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-900" data-testid="didit-not-configured">
            <div className="font-medium">Didit not configured</div>
            <div className="text-[10px] mt-0.5">Missing: {(health?.missing || []).join(", ")}. Set them in backend .env then restart.</div>
          </div>
        )}

        {!state?.has_verification && (
          <>
            <div className="text-[11px] text-slate-500 leading-relaxed">
              Sends {client.first_name || "the client"} a link to complete Didit's HMLR-compliant identity check on their phone: NFC passport scan, active liveness selfie, AML/PEP/sanctions screening, and proof of address.
            </div>
            <div className="p-2 rounded-md bg-slate-50 border border-slate-200 text-[10px] text-slate-600">
              <div className="font-medium text-slate-800 mb-0.5 flex items-center gap-1">
                <Fingerprint weight="fill" className="w-3 h-3" /> HMLR compliance policy
              </div>
              <ul className="space-y-0.5 list-disc pl-4">
                <li>NFC chip read (ePassport / national ID / UK BRP only — no driving licences)</li>
                <li>Active liveness selfie</li>
                <li>Sanctions + PEP + adverse media screening</li>
                <li>Proof of address (utility bill / bank statement &lt; 3 months)</li>
              </ul>
            </div>
            <Button
              size="sm" onClick={startVerification} disabled={starting || loading || !health?.configured}
              className="bg-indigo-600 hover:bg-indigo-700 text-white w-full h-8 text-xs"
              data-testid={`didit-start-${client.id}`}
            >
              <ShieldCheck weight="fill" className="w-3 h-3 mr-1" />
              {starting ? "Generating link…" : "Verify identity"}
            </Button>
          </>
        )}

        {state?.has_verification && state?.url && (state?.status || "").toLowerCase() !== "approved" && (
          <>
            <div className="p-2 rounded-md bg-slate-50 border border-slate-200 text-[11px]" data-testid="didit-link-box">
              <div className="text-slate-500 mb-1">Verification link — send to {client.email}:</div>
              <div className="font-mono text-[10px] text-slate-700 break-all">{state.url}</div>
            </div>
            {/* Send-to-client action row. "Email client" opens the
                default mail client with a pre-filled body — quickest
                for solicitors who read/send email locally.
                "Copy instructions" copies the same body to the
                clipboard for pasting into Outlook web / Gmail. */}
            <div className="flex flex-wrap gap-1.5">
              {client.email && (
                <Button size="sm" onClick={emailInstructions} className="h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`didit-email-${client.id}`}>
                  <Envelope weight="fill" className="w-3 h-3 mr-1" /> Email client
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={copyInstructions} className="h-7 text-[11px]" data-testid={`didit-copy-instructions-${client.id}`}>
                <Copy className="w-3 h-3 mr-1" /> Copy instructions
              </Button>
              <Button size="sm" variant="outline" onClick={copyLink} className="h-7 text-[11px]" data-testid={`didit-copy-${client.id}`}>
                <Copy className="w-3 h-3 mr-1" /> Copy link only
              </Button>
              <Button size="sm" variant="outline" onClick={refresh} className="h-7 text-[11px]" data-testid={`didit-refresh-${client.id}`}>
                <ArrowsClockwise className="w-3 h-3 mr-1" /> Refresh status
              </Button>
            </div>
          </>
        )}

        {state?.has_verification && ["approved", "declined", "in review", "in_review"].includes((state?.status || "").toLowerCase()) && (
          <div className="space-y-2">
            {/* Name re-split false positive — Didit flags a "middle name
                mismatch" when a UK passport's combined given names are
                split by the client. Offer the solicitor a one-click
                clear once the full name reconciles. */}
            {canApproveName && (
              <div className="p-2 rounded-md bg-indigo-50 border border-indigo-200 text-[11px] text-indigo-900" data-testid={`didit-name-review-${client.id}`}>
                <div className="font-medium flex items-center gap-1">
                  <Warning weight="fill" className="w-3 h-3" /> Name split flagged by Didit (likely false positive)
                </div>
                <div className="mt-1 text-[10px] leading-relaxed">
                  The passport reads <strong>{nameReview.extracted_given || "—"} {nameReview.extracted_surname || ""}</strong>.
                  The client split it into First <strong>{nameReview.confirmed_first || "—"}</strong>
                  {nameReview.confirmed_middle ? <> / Middle <strong>{nameReview.confirmed_middle}</strong></> : null}.
                  The full name matches — only the split differs. Confirm to clear the check.
                </div>
                <Button
                  size="sm" onClick={approveNameReview} disabled={approvingName}
                  className="mt-2 h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid={`didit-approve-name-review-${client.id}`}
                >
                  <CheckCircle weight="fill" className="w-3 h-3 mr-1" />
                  {approvingName ? "Clearing…" : "Confirm name & clear check"}
                </Button>
              </div>
            )}
            {state?.name_review_override && (
              <div className="p-2 rounded-md bg-emerald-50 border border-emerald-200 text-[10px] text-emerald-800" data-testid={`didit-name-review-cleared-${client.id}`}>
                <CheckCircle weight="fill" className="w-3 h-3 inline mr-1" />
                Name split confirmed as a false positive{state?.name_review_override_by ? ` by ${state.name_review_override_by}` : ""} — ID check cleared for onboarding.
              </div>
            )}
            {/* IN REVIEW — let the solicitor read the full Didit report in-app
                and approve/decline without logging into the Didit console.
                Approving pushes the decision to Didit AND updates onboarding. */}
            {["in review", "in_review"].includes((state?.status || "").toLowerCase()) && !state?.approved && (
              <div className="p-2.5 rounded-md bg-amber-50 border border-amber-200 space-y-2" data-testid={`didit-review-panel-${client.id}`}>
                <div className="text-[11px] font-medium text-amber-900 flex items-center gap-1">
                  <Warning weight="fill" className="w-3 h-3" /> This check is in review — read the report and decide here.
                </div>
                <div className="flex flex-wrap gap-2">
                  {state?.report_doc_id ? (
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => downloadAuthed(`/documents/${state.report_doc_id}/download`, "ID-verification-report.pdf").catch(() => toast.error("Could not download the report."))} data-testid={`didit-view-inreview-report-${client.id}`}>
                      <FileText className="w-3 h-3 mr-1" /> Download report
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={attachReport} disabled={attaching} data-testid={`didit-attach-inreview-report-${client.id}`}>
                      <FileText className="w-3 h-3 mr-1" /> {attaching ? "Fetching…" : "View report"}
                    </Button>
                  )}
                  <Button size="sm" onClick={() => decideReview("Approved")} disabled={deciding} className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`didit-approve-check-${client.id}`}>
                    <CheckCircle weight="fill" className="w-3 h-3 mr-1" /> {deciding ? "Saving…" : "Approve check"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => decideReview("Declined")} disabled={deciding} className="h-7 text-[11px] text-rose-700 border-rose-200 hover:bg-rose-50" data-testid={`didit-decline-check-${client.id}`}>
                    Decline
                  </Button>
                </div>
              </div>
            )}
            {state?.reviewed_in_app_by && (
              <div className="p-2 rounded-md bg-slate-50 border border-slate-200 text-[10px] text-slate-600" data-testid={`didit-reviewed-in-app-${client.id}`}>
                Reviewed in ConveyHub by {state.reviewed_in_app_by} ({state.reviewed_in_app_decision}).
              </div>
            )}
            {/* Split failures into blocking (red) and sandbox-info
                (blue). Sandbox notes explain why a criterion was
                skipped without making the check look failed. */}
            {(() => {
              const blocking = failures.filter((r) => !r.startsWith("[sandbox]"));
              const sandboxNotes = failures.filter((r) => r.startsWith("[sandbox]"));
              return (
                <>
                  {blocking.length > 0 && (
                    <div className="p-2 rounded-md bg-rose-50 border border-rose-200 text-[11px] text-rose-900" data-testid={`didit-failures-${client.id}`}>
                      <div className="font-medium flex items-center gap-1">
                        <XCircle weight="fill" className="w-3 h-3" /> Compliance failures
                      </div>
                      <ul className="mt-1 list-disc pl-4 space-y-0.5 text-[10px]">
                        {blocking.map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    </div>
                  )}
                  {sandboxNotes.length > 0 && (
                    <div className="p-2 rounded-md bg-sky-50 border border-sky-200 text-[11px] text-sky-900" data-testid={`didit-sandbox-notes-${client.id}`}>
                      <div className="font-medium">Sandbox notes</div>
                      <ul className="mt-1 list-disc pl-4 space-y-0.5 text-[10px]">
                        {sandboxNotes.map((r, i) => <li key={i}>{r.replace(/^\[sandbox\]\s*/, "")}</li>)}
                      </ul>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Didit-side decline reasons (per-feature warnings). Only
                renders if Didit itself flagged issues — separates our
                compliance-gate failures from Didit's own findings. */}
            {(evidence.decline_reasons || []).length > 0 && (
              <div className="p-2 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-900" data-testid={`didit-decline-reasons-${client.id}`}>
                <div className="font-medium flex items-center gap-1">
                  <Warning weight="fill" className="w-3 h-3" /> Didit findings
                </div>
                <ul className="mt-1 space-y-1 text-[10px]">
                  {(evidence.decline_reasons || []).map((r, i) => (
                    <li key={i} className="border-l-2 border-amber-300 pl-2">
                      <div className="font-medium">{r.feature}: {r.short}</div>
                      {r.long && <div className="text-amber-700 mt-0.5">{r.long}</div>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* AML hit detail — high-priority. If Didit found any
                sanction/PEP/adverse-media match, we render each hit
                with the name, list type, and score so the compliance
                officer has enough context to decide (this is what an
                MLRO would want to see before waving a case through). */}
            {(evidence.aml?.hits || []).length > 0 && (
              <div className="p-2 rounded-md bg-rose-50 border border-rose-200 text-[11px]" data-testid={`didit-aml-hits-${client.id}`}>
                <div className="font-medium text-rose-900 flex items-center gap-1">
                  <XCircle weight="fill" className="w-3 h-3" /> AML matches ({evidence.aml.total_hits || evidence.aml.hits.length})
                </div>
                <ul className="mt-1 space-y-1 text-[10px]">
                  {evidence.aml.hits.map((h, i) => (
                    <li key={i} className="bg-white border border-rose-200 rounded p-1.5">
                      <div className="font-medium text-slate-900">{h.name || "—"}</div>
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {(h.types || []).map((t, j) => (
                          <span key={j} className="inline-block text-[9px] uppercase bg-rose-100 text-rose-800 px-1 rounded">{t}</span>
                        ))}
                        {h.score != null && <span className="text-[9px] text-slate-500">score {h.score}</span>}
                      </div>
                      {h.date_of_birth && <div className="text-[9px] text-slate-500 mt-0.5">DOB {h.date_of_birth}</div>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Evidence table */}
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
              <div className="text-slate-500">Full name</div>
              <div className="text-slate-900 font-medium truncate">{evidence.full_name || "—"}</div>
              <div className="text-slate-500">DOB</div>
              <div className="text-slate-900">{evidence.date_of_birth || "—"}</div>
              <div className="text-slate-500">Nationality</div>
              <div className="text-slate-900">{evidence.nationality || "—"}</div>
              <div className="text-slate-500">Document</div>
              <div className="text-slate-900">
                {evidence.document_type || "—"} {evidence.document_number ? `· ${evidence.document_number}` : ""}
              </div>
              <div className="text-slate-500">NFC chip</div>
              <div className={evidence.nfc_verified ? "text-emerald-700 font-medium" : "text-rose-700 font-medium"}>
                {evidence.nfc_verified ? "✓ Verified" : "✗ Not read"}
              </div>
              <div className="text-slate-500">Liveness</div>
              <div className={(evidence.liveness?.method === "ACTIVE") ? "text-emerald-700" : "text-rose-700"}>
                {evidence.liveness?.method || "—"}
                {evidence.liveness?.score != null && ` · ${evidence.liveness.score}`}
              </div>
              <div className="text-slate-500">Face match</div>
              <div className="text-slate-900">
                {evidence.photo_comparison?.status || "—"}
                {evidence.photo_comparison?.score != null && ` · ${evidence.photo_comparison.score}`}
              </div>
              <div className="text-slate-500">AML</div>
              <div className={(evidence.aml?.status === "Approved") ? "text-emerald-700" : "text-rose-700"}>
                {evidence.aml?.status || "—"}
                {evidence.aml?.hits?.length > 0 && ` · ${evidence.aml.hits.length} hit(s)`}
              </div>
              <div className="text-slate-500">Address</div>
              <div className="text-slate-900 truncate" title={evidence.address?.address}>
                {evidence.address?.status || "—"}
              </div>
            </div>
            <div className="flex gap-1.5 pt-1 border-t border-slate-100">
              <Button size="sm" variant="outline" onClick={refresh} className="h-7 text-[11px]" data-testid={`didit-refresh-approved-${client.id}`}>
                <ArrowsClockwise className="w-3 h-3 mr-1" /> Refresh
              </Button>
              {state?.report_doc_id && (
                <Button
                  size="sm" variant="outline" asChild
                  className="h-7 text-[11px]"
                  data-testid={`didit-view-report-${client.id}`}
                >
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${state.report_doc_id}/download`}
                     target="_blank" rel="noreferrer">
                    <FileText className="w-3 h-3 mr-1" /> Evidence PDF
                  </a>
                </Button>
              )}
              {!state?.report_doc_id && (
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={attachReport} disabled={attaching}
                  data-testid={`didit-get-report-${client.id}`}>
                  <FileText className="w-3 h-3 mr-1" /> {attaching ? "Fetching…" : "Get report"}
                </Button>
              )}
            </div>
            {state?.report_doc_id && state?.report_kind === "summary_fallback" && (
              <p className="text-[10px] text-amber-700 pt-1" data-testid={`didit-report-fallback-note-${client.id}`}>
                This is our summary pack — Didit&apos;s full report wasn&apos;t available yet. Click <strong>Refresh</strong> to pull the official multi-page PDF.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
